import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { styled } from "@mui/material/styles";
import { Box, Button, TextField, Typography, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useResponsive from "../hooks/useResponsive";
import Logo from "../components/Logo";
import { ForgotPassword } from "../AuthenticationData.ts";
import SimpleFooter from "../components/SimpleFooter";

const StyledPageContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
});

const ContentWrapper = styled("div")(({ theme }) => ({
  flexGrow: 1,
  display: "flex",
  flexDirection: "column",

  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
}));

const StyledSection = styled("div")(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up("md")]: {
    width: "40%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3, 4),
    backgroundColor: theme.palette.background.default,
  },
}));

const StyledContent = styled("div")(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  padding: theme.spacing(8, 3, 2, 3),
  overflowY: "auto",
  [theme.breakpoints.up("md")]: {
    width: "35%",
    alignItems: "center",
    padding: theme.spacing(3, 4),
  },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: 480,
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(6, 5),
    boxShadow: theme.shadows[8],
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius * 2,
    border: `1px solid ${theme.palette.divider}`,
  },
}));

export default function ForgotPasswordEmail() {
  const navigate = useNavigate();
  const mdUp = useResponsive("up", "md");

  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [emailError, setEmailError] = useState("");

  const handleEmailChange = (event) => {
    const newEmail = event.target.value;
    setEmail(newEmail);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (newEmail.trim() !== "" && !emailRegex.test(newEmail)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const response = await ForgotPassword(email);
      if (response && response.status === 200) {
        localStorage.setItem("resetRequested", "true");
        setEmailSent(true);
        toast.success("Password reset instructions have been sent.", {
          position: "top-center",
        });
      } else {
        const errorMessage =
          response?.data?.error ||
          response?.data?.message ||
          "Error sending reset email.";
        toast.error(errorMessage, { position: "top-center" });
        setEmail("");
        setEmailError("");
      }
    } catch (error) {
      console.error("Error requesting password reset:", error);
      toast.error(
        "An unexpected error occurred. Please wait a moment and try again.",
        { position: "top-center" },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contactie | Forgot Password</title>
        <meta
          name="description"
          content="Reset your Contactie account password. Enter your email address to receive instructions."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <StyledPageContainer>
        {" "}
        <ContentWrapper>
          {" "}
          <Box
            component="header"
            sx={{
              display: { xs: "none", md: "block" },
              position: "absolute",
              zIndex: 10,
              top: 40,
              left: 40,
            }}
          >
            <Logo sx={{ height: 30, width: "auto" }} />
          </Box>
          {mdUp && (
            <StyledSection>
              <Typography
                variant="h4"
                sx={{ textAlign: "center", mb: 2, px: 5 }}
              >
                Forgot your password?
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "text.secondary", textAlign: "center" }}
              >
                Enter your email below, and we'll send you a link to reset your
                password.
              </Typography>
              <Box
                component="img"
                src="/assets/illustrations/illustration_forgot_password.svg"
                alt="Forgot password illustration"
                sx={{
                  width: "100%",
                  maxWidth: 320,
                  maxHeight: "50vh",
                  objectFit: "contain",
                  mt: 4,
                }}
              />
            </StyledSection>
          )}
          <StyledContent>
            <FormContainer>
              <Box
                sx={{
                  mb: 5,
                  display: { xs: "flex", md: "none" },
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <Logo sx={{ height: 35, width: "auto", mb: 3 }} />
                <Typography variant="h4" gutterBottom>
                  {emailSent ? "Request Sent!" : "Forgot Password?"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mb: 3 }}
                >
                  {emailSent ? (
                    <>
                      `A recovery link has been sent to <strong>{email}</strong>
                      . Please check your inbox.`
                    </>
                  ) : (
                    "Enter the email for your account to receive reset instructions."
                  )}
                </Typography>
                <Box
                  component="img"
                  src="/assets/illustrations/illustration_forgot_password.svg"
                  sx={{ height: 180 }}
                />
              </Box>

              <Box sx={{ display: { xs: "none", md: "block" }, mb: 3 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                  {emailSent ? "Request Sent!" : "Forgot your password?"}
                </Typography>
                <Typography sx={{ color: "text.secondary" }}>
                  {emailSent ? (
                    <>
                      A recovery link has been sent to <strong>{email}</strong>.
                      Please check your inbox.
                    </>
                  ) : (
                    "Please enter the email address associated with your account."
                  )}
                </Typography>
              </Box>
              {emailSent ? (
                <Button
                  fullWidth
                  size="large"
                  variant="contained"
                  onClick={() => navigate("/login")}
                >
                  Return to Login
                </Button>
              ) : (
                <>
                  <Box>
                    <TextField
                      fullWidth
                      name="email"
                      label="Email Address"
                      type="email"
                      value={email}
                      onChange={handleEmailChange} // Use the new handler
                      error={!!emailError} // Add this to show the error state
                      helperText={emailError} // Add this to display the error message
                      sx={{ mb: 3 }}
                    />
                    <Stack spacing={2}>
                      <Button
                        fullWidth
                        size="large"
                        variant="outlined" // In your provided code, you switched this with 'contained'. I'll stick to that.
                        onClick={handleConfirm}
                        // UPDATE THIS LINE
                        disabled={isSubmitting || !email.trim() || !!emailError}
                      >
                        {isSubmitting ? "Sending..." : "Send Reset Link"}
                      </Button>
                      <Button
                        fullWidth
                        size="large"
                        variant="contained"
                        onClick={() => navigate("/login")}
                        disabled={isSubmitting}
                      >
                        Return to Login
                      </Button>
                    </Stack>
                  </Box>
                </>
              )}
            </FormContainer>
          </StyledContent>
        </ContentWrapper>
        <SimpleFooter />
      </StyledPageContainer>
    </>
  );
}
